//TODO : Refactor after ai
/**
 * Arbeitsvorrat (Vorgänge, Dokumente) bzw. Verzeichnis (Stammdaten) einer
 * Objektklasse. Was der Screen zeigt, kommt aus dem Modell – er kennt keine
 * einzelne Objektklasse.
 */

import { useMemo, useState } from "react";
import { Plus, Smartphone } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { PagesRoutes } from "@app/router";
import { personaVon, useDatenStore } from "@app/store";
import {
  Badge,
  Button,
  Chip,
  ObjectOverview,
  type OverviewColumn,
  type OverviewGroup,
  type OverviewView,
  type SortDirection,
  type SortField,
} from "@shared/components/widgets/ooux";
import {
  ERLEDIGTE_ZUSTAENDE,
  anlegeAktion,
  datensaetze,
  gehoertZu,
  objektKlasse,
  statusFarbe,
  type Aktion,
  type ObjektKlasse,
} from "@shared/demo-data/viacid";
import { SatzFormular } from "./SatzFormular";
import { useTranslation } from "@shared/locales";
import { alsUebersichtsObjekt, terminFeld } from "./uebersichtsObjekt";

type Vorrat = "offen" | "meine" | "alle";
const KALENDER_ZUERST = new Set(["einsatz", "abwesenheit"]);

function ansichten(klasse: ObjektKlasse): OverviewView[] {
  const views: OverviewView[] =
    klasse.navigation === "stammdaten" ? ["table", "list"] : ["list", "table"];
  if (klasse.zustaende.length > 0) views.push("kanban");
  if (terminFeld(klasse)) views.push("calendar");
  if (KALENDER_ZUERST.has(klasse.slug))
    return ["calendar", ...views.filter((v) => v !== "calendar")];
  return views;
}

export function ObjektUebersichtPage() {
  const { slug = "" } = useParams<{ slug: string }>();
  const klasse = objektKlasse(slug);
  const { t } = useTranslation();

  if (!klasse) {
    return (
      <p style={{ color: "var(--text-tertiary)" }}>
        {t("viacid.unbekannteKlasse")}
      </p>
    );
  }
  return <Uebersicht key={klasse.slug} klasse={klasse} />;
}

interface UebersichtProps {
  klasse: ObjektKlasse;
}

function Uebersicht({ klasse }: UebersichtProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const views = ansichten(klasse);
  const [view, setView] = useState<OverviewView>(views[0]);
  const [query, setQuery] = useState("");
  const [zustaende, setZustaende] = useState<string[]>([]);
  const [sort, setSort] = useState("title");
  const [richtung, setRichtung] = useState<SortDirection>("asc");
  const bestand = useDatenStore((s) => s.bestand);
  const rolle = useDatenStore((s) => s.rolle);
  const persona = personaVon(rolle);
  const mitVorrat =
    klasse.zustaende.length > 0 && klasse.navigation !== "stammdaten";
  const [vorrat, setVorrat] = useState<Vorrat>(
    mitVorrat && views[0] !== "calendar" ? "offen" : "alle"
  );
  const [formular, setFormular] = useState<Aktion | null>(null);

  const saetze = datensaetze(bestand, klasse.slug);
  const imVorrat = (v: Vorrat) =>
    saetze.filter((d) =>
      v === "offen"
        ? d.status == null || !ERLEDIGTE_ZUSTAENDE.has(d.status)
        : v === "meine"
          ? persona != null && gehoertZu(klasse.slug, d, persona)
          : true
    );
  const alle = useMemo(
    () => imVorrat(vorrat).map((d) => alsUebersichtsObjekt(klasse, d)),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- imVorrat hängt nur an diesen Werten
    [klasse, bestand, vorrat, persona]
  );

  const items = useMemo(() => {
    const q = query.trim().toLowerCase();
    const gefiltert = alle.filter(
      (o) =>
        (zustaende.length === 0 ||
          (o.status != null && zustaende.includes(o.status))) &&
        (q === "" ||
          `${o.title} ${o.subtitle ?? ""} ${o.identifier ?? ""}`
            .toLowerCase()
            .includes(q))
    );
    const wert = (o: (typeof gefiltert)[number]): string =>
      sort === "status"
        ? String(klasse.zustaende.indexOf(o.status ?? "")).padStart(2, "0")
        : String(o[sort] ?? "");
    return [...gefiltert].sort(
      (a, b) =>
        wert(a).localeCompare(wert(b), "de-CH") * (richtung === "asc" ? 1 : -1)
    );
  }, [alle, query, zustaende, sort, richtung, klasse]);

  const sortFields: SortField[] = [
    { label: t("viacid.sortierung.titel"), value: "title" },
    ...(klasse.kennungFeld
      ? [{ label: t("viacid.sortierung.kennung"), value: "identifier" }]
      : []),
    ...(klasse.zustaende.length > 0
      ? [{ label: t("viacid.sortierung.status"), value: "status" }]
      : []),
  ];

  const spaltenLabels = alle[0]?.attributes?.map((a) => a.label) ?? [];
  const columns: OverviewColumn[] = [
    ...(klasse.kennungFeld
      ? [{ key: "identifier", header: t("viacid.spalten.kennung"), width: 140 }]
      : []),
    { key: "title", header: klasse.name },
    ...spaltenLabels.map((label) => ({
      key: label,
      header: label,
      sortable: false,
      render: (o: (typeof alle)[number]) =>
        o.attributes?.find((a) => a.label === label)?.value ?? "—",
    })),
    ...(klasse.zustaende.length > 0
      ? [
          {
            key: "status",
            header: t("viacid.spalten.status"),
            sortable: false,
            render: (o: (typeof alle)[number]) =>
              o.status ? (
                <Badge color={o.statusColor} dot>
                  {o.status}
                </Badge>
              ) : (
                "—"
              ),
          },
        ]
      : []),
  ];

  const groups: OverviewGroup[] = klasse.zustaende.map((z) => ({
    label: z,
    value: z,
    color: statusFarbe(klasse, z),
  }));
  const anlegen = anlegeAktion(klasse, rolle);
  const vorraete: { key: Vorrat; label: string }[] = [
    ...(mitVorrat
      ? [{ key: "offen" as const, label: t("viacid.vorrat.offen") }]
      : []),
    ...(persona
      ? [{ key: "meine" as const, label: t("viacid.vorrat.meine") }]
      : []),
    { key: "alle", label: t("viacid.vorrat.alle") },
  ];

  const toggle = (z: string) =>
    setZustaende((alt) =>
      alt.includes(z) ? alt.filter((x) => x !== z) : [...alt, z]
    );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <header
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 16,
        }}
      >
        <div>
          <h1
            style={{
              font: "var(--t-h6)",
              color: "var(--text-primary)",
              margin: 0,
            }}
          >
            {klasse.plural}
          </h1>
          <p
            style={{
              font: "var(--t-paragraph-m)",
              color: "var(--text-tertiary)",
              margin: "4px 0 0",
            }}
          >
            {klasse.beschreibung}
          </p>
        </div>
        {klasse.mobil && (
          <Badge color="violet" icon={<Smartphone size={14} />}>
            {t("viacid.mobil")}
          </Badge>
        )}
      </header>
      <ObjectOverview
        items={items}
        view={view}
        views={views}
        onViewChange={(v) => {
          setView(v);
          // Tafel und Kalender brauchen alle Zustände, sonst bleiben Spalten und vergangene Tage leer
          if ((v === "kanban" || v === "calendar") && vorrat === "offen")
            setVorrat("alle");
        }}
        query={query}
        onQueryChange={setQuery}
        searchPlaceholder={t("viacid.suche", { plural: klasse.plural })}
        countLabel={klasse.plural}
        sortFields={sortFields}
        sort={sort}
        sortDirection={richtung}
        onSortChange={setSort}
        onSortDirectionChange={setRichtung}
        columns={columns}
        groups={groups}
        filterBar={
          <div
            style={{
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            {vorraete.map((v) => (
              <Chip
                key={v.key}
                size="sm"
                selected={vorrat === v.key}
                onClick={() => setVorrat(v.key)}
              >
                {v.label} · {imVorrat(v.key).length}
              </Chip>
            ))}
            {klasse.zustaende.length > 0 && (
              <span
                style={{
                  width: 1,
                  height: 20,
                  background: "var(--neutral-200)",
                }}
              />
            )}
            {klasse.zustaende.map((z) => (
              <Chip
                key={z}
                size="sm"
                selected={zustaende.includes(z)}
                onClick={() => toggle(z)}
              >
                {z} · {alle.filter((o) => o.status === z).length}
              </Chip>
            ))}
          </div>
        }
        actions={
          anlegen ? (
            <Button
              size="sm"
              leadingIcon={<Plus size={16} />}
              onClick={() => setFormular(anlegen)}
            >
              {anlegen.name}
            </Button>
          ) : undefined
        }
        onOpen={(o) => navigate(PagesRoutes.objekte.show(klasse.slug, o.id))}
        emptyTitle={t("viacid.leer", { plural: klasse.plural })}
      />
      {formular && (
        <SatzFormular
          klasse={klasse}
          aktion={formular}
          onClose={(neu) => {
            setFormular(null);
            if (neu) navigate(PagesRoutes.objekte.show(klasse.slug, neu.id));
          }}
        />
      )}
    </div>
  );
}
